import { useMemo, useState } from "react";
import {
  deriveUnlocatedNumbers,
  isEstablishingShot,
  resolvePlanImage,
} from "./derive-precon-sections";
import type {
  DerivedPhotoEntry,
  DerivedSection,
  DerivedSelection,
  PreconPlanSource,
} from "./derive-precon-sections";
import { PreconPlanWithPins } from "./precon-plan-with-pins";
import {
  PreconPhotoLightbox,
  PreconPhotoThumbButton,
  type LightboxPhoto,
} from "./precon-photo-lightbox";
import { toFriendlyDate } from "./precon-dates";
import type {
  PreconCover,
  PreconSectionMeta,
  PreconSignatory,
} from "./precon-types";

interface Props {
  cover: PreconCover;
  headerBanner: string;
  /** Project logo URL (null = none); shown on the cover with an Otus fallback. */
  logo: string | null;
  derived: DerivedSelection;
  /** Ordered list of section plan_ids — the document's render order. */
  order: string[];
  /** The project's plans — source of each section's plan image. */
  plans: ReadonlyArray<PreconPlanSource>;
  /** Per-plan section metadata, keyed by plan_id. */
  sectionMeta: Record<string, PreconSectionMeta>;
  /** Per-photo caption overrides, keyed by image_id. */
  captions: Record<string, string>;
}

/** Whether a signatory survives the payload's keepSignatory rule (needs a name). */
const hasName = (s?: PreconSignatory): s is PreconSignatory =>
  Boolean(s && s.name.trim());

/** A muted em dash for an empty optional field — the read-only "nothing here". */
function Blank() {
  return <span className="text-muted-foreground">—</span>;
}

/** One labelled row of a read-only metadata table; omitted by the caller when empty. */
function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b last:border-0">
      <th
        scope="row"
        className="w-32 py-1.5 pr-3 text-left align-top text-xs font-medium text-muted-foreground"
      >
        {label}
      </th>
      <td className="py-1.5 text-sm">{value}</td>
    </tr>
  );
}

/** Read-only signatory block (name + designation) for the sign-off table. */
function Signatory({ value }: { value?: PreconSignatory }) {
  if (!hasName(value)) return <Blank />;
  return (
    <div className="space-y-0.5 text-sm">
      <p className="font-medium">{value.name.trim()}</p>
      {value.designation.trim() ? (
        <p className="text-xs text-muted-foreground">
          {value.designation.trim()}
        </p>
      ) : null}
    </div>
  );
}

/** Read-only cover sheet — mirrors precon-cover-step's print layout, no inputs. */
function CoverSheet({
  cover,
  headerBanner,
  logo,
}: {
  cover: PreconCover;
  headerBanner: string;
  logo: string | null;
}) {
  const showPrepared = hasName(cover.prepared_by);
  const showApproved = hasName(cover.approved_by);
  const hasSignatories = showPrepared || showApproved;
  const hasRevisions = cover.revisions.length > 0;

  return (
    <section
      aria-label="Cover"
      className="mx-auto w-full max-w-3xl rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6"
    >
      {/* Print header row: banner strip top-center, project logo top-right. */}
      <div className="flex items-start gap-4">
        <div aria-hidden className="hidden w-20 shrink-0 sm:block" />
        <div className="min-w-0 flex-1 text-center text-sm font-medium">
          {headerBanner.trim() ? headerBanner : <Blank />}
        </div>
        {/* Logo slot — display only; shows the project's own logo when set,
            falling back to a dashed placeholder noting the Otus logo the
            generated PDF uses when the project has none. */}
        {logo ? (
          <img
            src={logo}
            alt="Project logo"
            className="h-12 w-20 shrink-0 rounded-md border object-contain p-1"
          />
        ) : (
          <div className="flex h-12 w-20 shrink-0 flex-col items-center justify-center rounded-md border border-dashed text-[9px] leading-tight font-medium tracking-wide text-muted-foreground uppercase">
            Logo
            <span className="mt-0.5 normal-case opacity-70">Otus fallback</span>
          </div>
        )}
      </div>

      {/* Centered title. */}
      <h2 className="mt-8 text-center text-xl font-semibold">
        {cover.title.trim() ? cover.title : <Blank />}
      </h2>

      {/* Project description — only when present (optional; empty doesn't print). */}
      {cover.project_description.trim() && (
        <p className="mt-6 text-sm whitespace-pre-wrap">
          {cover.project_description}
        </p>
      )}

      {/* Report subtitle — only when present. */}
      {cover.report_subtitle.trim() && (
        <p className="mt-6 text-center text-sm text-muted-foreground">
          {cover.report_subtitle}
        </p>
      )}

      {/* Merged sign-off table — revisions as rows, the Prepared/Approved
          signatories echoed beside every row, exactly as the cover prints.
          Nameless signatories are dropped (matching keepSignatory). */}
      <div className="mt-8">
        <table
          aria-label="Sign-off table"
          className="w-full border-collapse text-sm"
        >
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th scope="col" className="w-14 px-2 pb-1.5 font-medium">
                Rev
              </th>
              <th scope="col" className="px-2 pb-1.5 font-medium">
                Description
              </th>
              <th scope="col" className="w-28 px-2 pb-1.5 font-medium">
                Date
              </th>
              {showPrepared && (
                <th scope="col" className="w-36 px-2 pb-1.5 font-medium">
                  Prepared by
                </th>
              )}
              {showApproved && (
                <th scope="col" className="w-36 px-2 pb-1.5 font-medium">
                  Approved by
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {!hasRevisions ? (
              <tr className="border-b align-top">
                <td
                  colSpan={3 + (showPrepared ? 1 : 0) + (showApproved ? 1 : 0)}
                  className="px-2 py-3 text-xs text-muted-foreground italic"
                >
                  No revisions — the sign-off table prints bare.
                </td>
              </tr>
            ) : (
              cover.revisions.map((r, i) => (
                <tr key={i} className="border-b align-top">
                  <td className="px-2 py-2 tabular-nums">
                    {r.rev.trim() || <Blank />}
                  </td>
                  <td className="px-2 py-2">{r.description.trim() || <Blank />}</td>
                  {/* Same friendly-date transform the payload applies before
                      submit, so the preview shows "1 Apr 2026", not the raw
                      ISO a <input type="date"> produces. */}
                  <td className="px-2 py-2">
                    {toFriendlyDate(r.date.trim()) || <Blank />}
                  </td>
                  {showPrepared && (
                    <td className="px-2 py-2">
                      <Signatory value={cover.prepared_by} />
                    </td>
                  )}
                  {showApproved && (
                    <td className="px-2 py-2">
                      <Signatory value={cover.approved_by} />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* When there are no revision rows the signatories still need to show;
            mirror the printed pair beneath the (bare) table. */}
        {!hasRevisions && hasSignatories && (
          <div className="mt-3 grid grid-cols-2 gap-4">
            {showPrepared && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  Prepared by
                </p>
                <Signatory value={cover.prepared_by} />
              </div>
            )}
            {showApproved && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  Approved by
                </p>
                <Signatory value={cover.approved_by} />
              </div>
            )}
          </div>
        )}
      </div>

    </section>
  );
}

/** A single `(n)` / `(na)` located-photo row: thumbnail + resolved caption. */
function LocatedRow({
  entry,
  caption,
  onPhotoOpen,
}: {
  entry: DerivedPhotoEntry;
  caption: string;
  onPhotoOpen: (photo: DerivedPhotoEntry, label: string) => void;
}) {
  // The record's pin number, sub-lettered per photo (Na, Nb…); a single-photo
  // record reads the bare number.
  const label = `(${entry.recordNumber}${entry.subLetter ?? ""})`;
  return (
    <li className="flex items-start gap-3">
      <span className="w-8 shrink-0 pt-1 text-sm font-medium tabular-nums text-muted-foreground">
        {label}
      </span>
      <PreconPhotoThumbButton photo={entry} label={label} onOpen={onPhotoOpen} />
      <span className="pt-1 text-sm">
        {caption.trim() || (
          <span className="text-muted-foreground italic">Untitled photo</span>
        )}
      </span>
    </li>
  );
}

/** A single `(U#)` / `(U#a)` not-located row: thumbnail + caption (no numbered pin). */
function UnlocatedRow({
  photo,
  label,
  caption,
  onPhotoOpen,
}: {
  photo: DerivedPhotoEntry;
  label: string;
  caption: string;
  onPhotoOpen: (photo: DerivedPhotoEntry, label: string) => void;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-10 shrink-0 pt-1 text-sm font-medium tabular-nums text-muted-foreground">
        {label}
      </span>
      <PreconPhotoThumbButton photo={photo} label={label} onOpen={onPhotoOpen} />
      <span className="pt-1 text-sm text-muted-foreground">
        {caption.trim() || "Untitled photo"}
      </span>
    </li>
  );
}

/** Read-only render of one plan-scoped section: heading, meta, notes, plan+pins, photos. */
function SectionView({
  section,
  meta,
  plan,
  captions,
  uNumbers,
  onPhotoOpen,
}: {
  section: DerivedSection;
  meta: PreconSectionMeta | undefined;
  plan: PreconPlanSource | undefined;
  captions: Record<string, string>;
  uNumbers: ReadonlyMap<string, number>;
  onPhotoOpen: (photo: DerivedPhotoEntry, label: string) => void;
}) {
  const planImage = resolvePlanImage(plan);
  // Build the metadata rows that have a value — empty ones simply don't render
  // (matching the PDF: optional, empty fields don't print).
  const metaRows: Array<{ label: string; value: string }> = [];
  if (meta?.precon_date.trim())
    // Same friendly-date transform the payload applies before submit, so the
    // preview shows "1 Apr 2026", not the raw ISO from a <input type="date">.
    metaRows.push({
      label: "Pre-con date",
      value: toFriendlyDate(meta.precon_date.trim()),
    });
  if (meta?.level.trim()) metaRows.push({ label: "Level", value: meta.level });
  if (meta?.grid_lines.trim())
    metaRows.push({ label: "Grid lines", value: meta.grid_lines });
  if (meta?.location.trim())
    metaRows.push({ label: "Location", value: meta.location });
  const notes = (meta?.notes ?? []).filter((n) => n.trim());

  return (
    <section
      aria-label={section.planName}
      className="mx-auto w-full max-w-3xl space-y-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6"
    >
      <h2 className="text-lg font-semibold">{section.planName}</h2>

      <div className={planImage ? "grid gap-5 md:grid-cols-2" : "space-y-4"}>
        {planImage && (
          <div className="md:order-2">
            <PreconPlanWithPins
              planImage={planImage}
              located={section.located}
              planName={section.planName}
            />
          </div>
        )}

        <div className="space-y-4 md:order-1">
          {metaRows.length > 0 && (
            <table className="w-full border-collapse text-sm">
              <tbody>
                {metaRows.map((row) => (
                  <MetaRow key={row.label} label={row.label} value={row.value} />
                ))}
              </tbody>
            </table>
          )}

          {notes.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Notes</p>
              <ol className="list-decimal space-y-1 pl-5 text-sm">
                {notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>

      {/* Located photos — numbered (n)/(na) to match the plan pins. */}
      {section.located.length > 0 && (
        <ul className="space-y-2">
          {section.located.map((entry) => (
            <LocatedRow
              key={entry.image_id}
              entry={entry}
              caption={captions[entry.image_id] ?? (isEstablishingShot(entry) ? entry.remark : "")}
              onPhotoOpen={onPhotoOpen}
            />
          ))}
        </ul>
      )}

      {/* Plan-but-unpinned photos — the "Not located on this plan" group. */}
      {section.unpinned.length > 0 && (
        <div className="space-y-2 rounded-md border border-dashed p-3">
          <p className="text-xs font-medium text-muted-foreground">
            Not located on this plan
          </p>
          <ul className="space-y-2">
            {section.unpinned.map((entry) => (
              <UnlocatedRow
                key={entry.image_id}
                photo={entry}
                label={`(U${uNumbers.get(entry.record_id) ?? "?"}${entry.subLetter ?? ""})`}
                caption={captions[entry.image_id] ?? (isEstablishingShot(entry) ? entry.remark : "")}
                onPhotoOpen={onPhotoOpen}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Read-only, content-level approximation of the assembled Pre-Condition Survey
 * (US-008). Renders the whole document — cover → each plan section (metadata,
 * notes, plan image with numbered pins, `(n)` / `(U#)` photo rows) → General
 * Observations → closing — as one continuous scroll, derived entirely from the
 * same state Generate sends (no separate data path that could drift).
 *
 * Deliberately NOT paginated and NOT pixel-true: no page breaks, no 4×2 grid
 * pages, no per-page chrome, no font/measure matching — the generated PDF is
 * the source of truth, and the persistent watermark says so (see the builder
 * spec, Layout § "Preview surface" and Out of Scope § "A pixel-true /
 * paginated PDF preview"). Surface (sheet vs. panel) is the shell's concern;
 * this component just renders the scroll.
 */
export function PreconPreview({
  cover,
  headerBanner,
  logo,
  derived,
  order,
  plans,
  sectionMeta,
  captions,
}: Props) {
  const { sections, unlocated } = derived;
  const planById = useMemo(
    () => new Map(plans.map((p) => [p.id, p])),
    [plans],
  );
  const sectionById = useMemo(
    () => new Map(sections.map((s) => [s.plan_id, s])),
    [sections],
  );

  // The document's continuous U# sequence for the CURRENT order — the same
  // walk the Sections step and the renderer use.
  const uNumbers = useMemo(
    () => deriveUnlocatedNumbers(derived, order),
    [derived, order],
  );

  // The preview owns its own lightbox instance (kept simple; it's a leaf of
  // the read-only tree, so it never collides with the Sections-step one).
  const [lightbox, setLightbox] = useState<LightboxPhoto | null>(null);
  const openPhoto = (photo: DerivedPhotoEntry, label: string) =>
    setLightbox({ photo, label });

  const hasSections = sections.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Persistent watermark — a fixed strip above the scroll region (it is a
          shrink-0 flex sibling, not inside the overflow child, so it stays
          pinned without `sticky`), keeping the approximation from ever being
          mistaken for the final PDF (US-008). `pointer-events-none` so this
          decorative banner never intercepts clicks meant for the Sheet's close
          button in the top-right corner; `pr-12` keeps the text clear of it. */}
      <div
        role="note"
        className="pointer-events-none shrink-0 border-b border-amber-300/60 bg-amber-100/90 px-4 py-2 pr-12 text-center text-xs font-medium text-amber-900 backdrop-blur dark:border-amber-400/30 dark:bg-amber-950/80 dark:text-amber-200"
      >
        Layout approximation — the generated PDF is the source of truth.
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        <div className="space-y-6">
          <CoverSheet
            cover={cover}
            headerBanner={headerBanner}
            logo={logo}
          />

          {order.map((planId) => {
            const section = sectionById.get(planId);
            if (!section) return null;
            return (
              <SectionView
                key={planId}
                section={section}
                meta={sectionMeta[planId]}
                plan={planById.get(planId)}
                captions={captions}
                uNumbers={uNumbers}
                onPhotoOpen={openPhoto}
              />
            );
          })}

          {/* General Observations — no-plan photos, continuing the U# walk. */}
          {unlocated.length > 0 && (
            <section
              aria-label="General Observations"
              className="mx-auto w-full max-w-3xl space-y-3 rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6"
            >
              <h2 className="text-lg font-semibold">General Observations</h2>
              <ul className="space-y-2">
                {unlocated.map((entry) => (
                  <UnlocatedRow
                    key={entry.image_id}
                    photo={entry}
                    label={`(U${uNumbers.get(entry.record_id) ?? "?"}${entry.subLetter ?? ""})`}
                    caption={captions[entry.image_id] ?? (isEstablishingShot(entry) ? entry.remark : "")}
                    onPhotoOpen={openPhoto}
                  />
                ))}
              </ul>
            </section>
          )}

          {/* US-007: a survey needs at least one plan with photos. The preview
              still shows the cover (and any General Observations above) — it
              never blanks — but states the stop. */}
          {!hasSections && (
            <p className="mx-auto w-full max-w-3xl text-center text-sm text-muted-foreground">
              A Pre-Condition Survey needs at least one plan with photos. Pin
              some of your selected photos to a plan to add a section.
            </p>
          )}

          {/* Closing line — matches the renderer's closing. */}
          <p className="mx-auto w-full max-w-3xl pt-2 text-center text-sm text-muted-foreground">
            — Thank you —
          </p>
        </div>
      </div>

      <PreconPhotoLightbox open={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
